import React from 'react';
import { FeatureCard } from '@components/Card';
import Button from '@components/Button';
import InteractiveMap from '@components/InteractiveMap';
import styles from './ProductPage.module.css';

const integrations = [
  { title: 'SCADA Systems', description: 'Real-time data ingestion from OSIsoft PI, Ignition, Wonderware, and other SCADA platforms.' },
  { title: 'ERP & EAM', description: 'Bi-directional sync with SAP, Oracle, Maximo, and other enterprise asset systems.' },
  { title: 'Fleet Management', description: 'GPS and telematics integration with Samsara, Verizon Connect, and Geotab.' },
  { title: 'Communication', description: 'Automated notifications via email, SMS, Microsoft Teams, and Slack.' },
  { title: 'Business Intelligence', description: 'Export to Power BI, Tableau, and custom dashboards via our API.' },
  { title: 'Identity & Access', description: 'SSO support for Okta, Azure AD, and Google Workspace.' },
];

const knowledgeSources = [
  { label: 'Service Manuals' },
  { label: 'P&IDs' },
  { label: 'Work Orders' },
  { label: 'Sensor Data' },
  { label: 'Tech Notes' },
  { label: 'Training Docs' },
];

const ProductPage: React.FC = () => {
  return (
    <>
      {/* AI Assistant Hero */}
      <section className={styles.productHero} id="assistant">
        <div className={styles.productHeroContent}>
          <div className={styles.productHeroText}>
            <div className="section-label">AI Assistant</div>
            <h1>Your technicians' <span className="highlight">smartest co-pilot</span></h1>
            <p>An intelligent chatbot that knows your operations inside and out. Powered by RAG technology trained on your sensor data, service manuals, and historical records.</p>
            <Button variant="primary" size="large" to="/contact#demo">Request Demo</Button>
          </div>
          <div className={styles.productVisual}>
            <div className={styles.chatDemo}>
              <div className={styles.chatHeader}>
                <div className={styles.chatStatus}></div>
                <div className={styles.chatTitle}>Laminar Assistant</div>
              </div>
              <div className={styles.chatMessages}>
                <div className={`${styles.chatMessage} ${styles.user}`}>
                  Compressor C-204 is showing high discharge temp. What should I check first?
                </div>
                <div className={`${styles.chatMessage} ${styles.assistant}`}>
                  For high discharge temps on the Ariel JGK-4, check these in order:
                  <br /><br />
                  1. <strong>Suction valve condition</strong> — worn valves are the #1 cause
                  <br />
                  2. <strong>Cooling water flow</strong> — verify 45+ GPM
                  <br />
                  3. <strong>Gas composition</strong> — check for liquid carryover
                  <br /><br />
                  Based on C-204's history, the suction valve was last replaced 14 months ago. Would you like me to pull up the valve replacement procedure?
                </div>
                <div className={`${styles.chatMessage} ${styles.user}`}>
                  Yes, show me the procedure
                </div>
              </div>
              <div className={styles.chatInput}>
                <input type="text" placeholder="Ask about equipment, procedures, or troubleshooting..." readOnly />
                <button>→</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Map Section */}
      <section className={styles.productSection} id="map">
        <div className={`${styles.productSectionContent} ${styles.reverse}`}>
          <div className={styles.productSectionText}>
            <div className="section-label">Live Operations Map</div>
            <h2>See everything. Miss nothing.</h2>
            <p>Real-time visibility into every unit, sensor, and technician across your operations. Color-coded status indicators and AI-powered alerts ensure you're always focused on what matters most.</p>
            <ul className="feature-list">
              <li>Predictive maintenance algorithms</li>
              <li>Unit status with sensor data overlays</li>
              <li>Alerts surfaced directly on map</li>
              <li>One-click dispatch to nearest available tech</li>
              <li>In depth incident analysis</li>
            </ul>
          </div>
          <div className={styles.productVisual}>
            <InteractiveMap />
          </div>
        </div>
      </section>

      {/* Dispatcher Tools Section */}
      <section className={styles.productSection}>
        <div className={styles.productSectionContent}>
          <div className={styles.productSectionText}>
            <div className="section-label">Dispatcher Console</div>
            <h2>Dispatch smarter, not harder</h2>
            <p>AI-optimized routing and scheduling that considers technician skills, location, parts availability, and job priority. Reduce windshield time and get the right person to the right job.</p>
            <ul className="feature-list">
              <li>Skill-based technician matching</li>
              <li>Route optimization with traffic awareness</li>
              <li>Parts inventory check before dispatch</li>
              <li>Turbocharge work order generation</li>
              <li>Workload balancing across teams</li>
            </ul>
          </div>
          <div className={styles.productVisual}>
            <div className={styles.dashboardPreview}>
              <div className={styles.dashboardHeader}>
                <span className={`${styles.dashboardDot} ${styles.red}`}></span>
                <span className={`${styles.dashboardDot} ${styles.yellow}`}></span>
                <span className={`${styles.dashboardDot} ${styles.green}`}></span>
              </div>
              <div className={styles.dashboardContent}>
                <div className={styles.dispatchPreview}>
                  <div className={styles.dispatchCard}>
                    <div className={styles.dispatchStatus}>
                      <span className={styles.statusDot}></span>
                      Available
                    </div>
                    <div className={styles.dispatchName}>John Martinez</div>
                    <div className={styles.dispatchDetail}>15 min away • Has parts</div>
                  </div>
                  <div className={styles.dispatchCard}>
                    <div className={styles.dispatchStatus}>
                      <span className={`${styles.statusDot} ${styles.busy}`}></span>
                      On Job
                    </div>
                    <div className={styles.dispatchName}>Sarah Chen</div>
                    <div className={styles.dispatchDetail}>Finishing in 45 min</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Knowledge Engine Section */}
      <section className={`${styles.productSection} ${styles.alternate}`}>
        <div className={`${styles.productSectionContent} ${styles.reverse}`}>
          <div className={styles.productSectionText}>
            <div className="section-label">Knowledge Engine</div>
            <h2>Every answer, instantly</h2>
            <p>Our RAG-powered AI connects to your entire knowledge base, including service manuals, work order history, sensor data, and historical knowledge, to give technicians instant, accurate answers.</p>
            <ul className="feature-list">
              <li>Equipment-specific context awareness</li>
              <li>Learns from resolved work orders</li>
              <li>Supports images, diagrams, and schematics</li>
              <li>Continuous improvement from technician feedback</li>
            </ul>
          </div>
          <div className={styles.productVisual}>
            <div className={styles.knowledgeVisual}>
              <div className={styles.knowledgeGrid}>
                {knowledgeSources.map((source) => (
                  <div key={source.label} className={styles.knowledgeSource}>
                    <div className={styles.knowledgeLabel}>{source.label}</div>
                  </div>
                ))}
              </div>
              <div className={styles.knowledgeArrow}>
                <div className={styles.arrowLine}></div>
                <div className={styles.arrowIcon}>↓</div>
                <div className={styles.arrowLine}></div>
              </div>
              <div className={styles.knowledgeEngine}>
                <div className={styles.engineText}>
                  <div className={styles.engineTitle}>Laminar Knowledge Engine</div>
                  <div className={styles.engineSubtitle}>Instant answers from your entire knowledge base</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className={styles.integrationsSection}>
        <div className="container">
          <div className="section-header">
            <div className="section-label">Integrations</div>
            <h2 className="section-title">Works with your stack</h2>
            <p className="section-subtitle">Connect Laminar to the tools you already use. No rip-and-replace required.</p>
          </div>
          <div className={styles.integrationsGrid}>
            {integrations.map((integration, index) => (
              <FeatureCard
                key={integration.title}
                title={integration.title}
                description={integration.description}
                animationDelay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductPage;